<template>
  <check-authorization :requiresAuthorizations="['listar permisos']">
    <div class="card">
      <div class="card-header permissions__header">
        <div>
          Permisos
          <div class="permissions__summary">
            {{ totalPermisos }} permisos en {{ modulos.length }} módulos. El nombre es fijo porque el sistema lo
            usa para dar acceso; la descripción es la que se ve en la pantalla de roles.
          </div>
        </div>

        <b-input-group size="sm" class="permissions__search">
          <b-input-group-prepend is-text><b-icon-search /></b-input-group-prepend>
          <b-form-input v-model="filter" type="search" placeholder="Buscar por nombre, descripción o rol" />
        </b-input-group>
      </div>

      <b-overlay :show="loading" rounded="sm">
        <div class="card-body p-0">
          <div class="permissions__tools">
            <b-link class="small" @click="setAll(true)">Expandir todo</b-link>
            <span class="text-muted">·</span>
            <b-link class="small" @click="setAll(false)">Contraer todo</b-link>
          </div>

          <section v-for="modulo in visibleModulos" :key="modulo.nombre" class="permissions__module">
            <button type="button" class="permissions__module-toggle" @click="toggle(modulo.nombre)">
              <b-icon :icon="isOpen(modulo.nombre) ? 'chevron-down' : 'chevron-right'" class="mr-2" />
              <span class="permissions__module-name">{{ modulo.nombre }}</span>
              <b-badge variant="light" pill class="ml-2">{{ modulo.visibles.length }}</b-badge>
            </button>

            <table v-if="isOpen(modulo.nombre)" class="permissions__table">
              <tbody>
                <tr v-for="p in modulo.visibles" :key="p.id">
                  <td class="permissions__name">
                    <code>{{ p.name }}</code>
                  </td>

                  <td class="permissions__description">
                    <div v-if="editing.id === p.id" class="permissions__edit">
                      <b-form-input
                        v-model="editing.text"
                        size="sm"
                        maxlength="255"
                        autofocus
                        :disabled="editing.saving"
                        @keyup.enter="saveDescription(p)"
                        @keyup.esc="cancelEdit"
                      />
                      <b-button size="sm" variant="primary" :disabled="editing.saving || !editing.text.trim()" @click="saveDescription(p)">
                        Guardar
                      </b-button>
                      <b-button size="sm" variant="light" :disabled="editing.saving" @click="cancelEdit">
                        Cancelar
                      </b-button>
                    </div>
                    <template v-else>
                      <span :class="{ 'text-muted font-italic': !p.description }">
                        {{ p.description || 'Sin descripción' }}
                      </span>
                      <button
                        v-if="canEdit"
                        type="button"
                        class="permissions__edit-btn"
                        title="Editar descripción"
                        @click="startEdit(p)"
                      >
                        <b-icon-pencil />
                      </button>
                    </template>
                  </td>

                  <td class="permissions__roles">
                    <span v-for="r in rolesVisibles(p.id)" :key="r" class="permissions__role">{{ r }}</span>
                    <button
                      v-if="(rolesPorPermiso[p.id] || []).length > 3"
                      type="button"
                      class="permissions__more"
                      :title="rolesPorPermiso[p.id].join(', ')"
                      @click="$set(expandedRoles, p.id, !expandedRoles[p.id])"
                    >
                      {{ expandedRoles[p.id] ? 'ver menos' : `+${rolesPorPermiso[p.id].length - 2} más` }}
                    </button>
                    <span v-if="!rolesPorPermiso[p.id]" class="text-muted">Ningún rol (solo admin)</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <p v-if="!loading && !visibleModulos.length" class="text-muted small text-center my-4">
            Ningún permiso coincide con la búsqueda.
          </p>
        </div>
      </b-overlay>
    </div>
  </check-authorization>
</template>

<script>
import { mapGetters } from 'vuex'

const normalize = text =>
  (text || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

const editing = () => ({ id: null, text: '', saving: false })

export default {
  name: 'Permissions',

  data () {
    return {
      loading: false,
      roles: [],
      modulos: [],
      filter: '',
      open: {},
      expandedRoles: {},
      editing: editing()
    }
  },

  computed: {
    ...mapGetters('userModule', ['getAuthPermissions']),

    canEdit () {
      return this.getAuthPermissions.includes('editar permisos')
    },

    totalPermisos () {
      return this.modulos.reduce((total, m) => total + m.permisos.length, 0)
    },

    /**
     * Roles que tienen cada permiso (sin admin, que los tiene todos).
     */
    rolesPorPermiso () {
      const map = {}
      this.roles
        .filter(r => r.name !== 'admin')
        .forEach(role => {
          role.permisos.forEach(id => {
            if (!map[id]) map[id] = []
            map[id].push(role.name)
          })
        })
      return map
    },

    visibleModulos () {
      const text = normalize(this.filter)

      return this.modulos
        .map(modulo => {
          const moduloCoincide = text && normalize(modulo.nombre).includes(text)
          const visibles = modulo.permisos.filter(p => {
            if (!text || moduloCoincide) return true
            return (
              normalize(p.name).includes(text) ||
              normalize(p.description).includes(text) ||
              (this.rolesPorPermiso[p.id] || []).some(r => normalize(r).includes(text))
            )
          })
          return { ...modulo, visibles }
        })
        .filter(m => m.visibles.length)
    }
  },

  watch: {
    filter () {
      this.open = {}
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      const { error, message, data } = await this.$store.dispatch('permissionModule/getCatalogo')
      this.loading = false

      if (error) return this.$notify({ error, message }, 'Permisos')

      this.roles = data.data.roles
      this.modulos = data.data.modulos
    },

    // Al buscar, los módulos que coinciden se abren solos; sin búsqueda empiezan cerrados.
    isOpen (nombre) {
      return this.open[nombre] !== undefined ? this.open[nombre] : Boolean(this.filter)
    },

    toggle (nombre) {
      this.$set(this.open, nombre, !this.isOpen(nombre))
    },

    setAll (value) {
      this.modulos.forEach(m => this.$set(this.open, m.nombre, value))
    },

    // Con más de tres roles se muestran dos y un "+N más" para no alargar la fila.
    rolesVisibles (id) {
      const roles = this.rolesPorPermiso[id] || []
      return roles.length > 3 && !this.expandedRoles[id] ? roles.slice(0, 2) : roles
    },

    startEdit (permiso) {
      this.editing = { ...editing(), id: permiso.id, text: permiso.description || '' }
    },

    cancelEdit () {
      this.editing = editing()
    },

    async saveDescription (permiso) {
      const text = this.editing.text.trim()
      if (!text || this.editing.saving) return

      this.editing.saving = true
      const { error, message } = await this.$store.dispatch('permissionModule/updatePermission', {
        id: permiso.id,
        payload: { description: text }
      })
      this.editing.saving = false

      this.$notify({ error, message }, 'Permisos')
      if (error) return

      permiso.description = text
      this.cancelEdit()
    }
  }
}
</script>

<style lang="scss" scoped>
.permissions__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.permissions__summary {
  font-size: 0.75rem;
  font-weight: 400;
  color: #69707a;
  max-width: 640px;
}

.permissions__search {
  max-width: 320px;
}

.permissions__tools {
  display: flex;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #e3e6ec;
}

.permissions__module {
  border-bottom: 1px solid #e3e6ec;
}

.permissions__module-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 1rem;
  background: #f8f9fb;
  border: 0;
  text-align: left;
  font-size: 0.85rem;

  &:hover {
    background: #f2f4f8;
  }

  &:focus {
    outline: none;
  }
}

.permissions__module-name {
  font-weight: 600;
  color: #212832;
}

.permissions__table {
  width: 100%;
  font-size: 0.8rem;

  td {
    padding: 0.4rem 1rem;
    border-top: 1px solid #f2f4f8;
    vertical-align: middle;
  }
}

.permissions__name {
  width: 28%;

  code {
    font-size: 0.75rem;
    color: #4a515b;
    background: #f2f4f8;
    padding: 0.1rem 0.35rem;
    border-radius: 0.25rem;
    white-space: nowrap;
  }
}

.permissions__description {
  width: 42%;
  color: #212832;

  &:hover .permissions__edit-btn {
    opacity: 1;
  }
}

.permissions__edit {
  display: flex;
  gap: 0.35rem;
}

.permissions__edit-btn {
  border: 0;
  background: none;
  color: #69707a;
  padding: 0 0.3rem;
  opacity: 0.35;

  &:hover,
  &:focus {
    color: #0061f2;
    opacity: 1;
    outline: none;
  }
}

.permissions__roles {
  width: 30%;
}

.permissions__more {
  border: 0;
  background: none;
  padding: 0 0.2rem;
  font-size: 0.7rem;
  color: #0061f2;

  &:focus {
    outline: none;
  }
}

.permissions__role {
  display: inline-block;
  font-size: 0.7rem;
  line-height: 1.4;
  padding: 0.1rem 0.45rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
  border-radius: 1rem;
  background: #eef2ff;
  color: #3a4bb3;
}
</style>
